import { API_ROUTES } from "src/router/api";
import { boot } from "quasar/wrappers";
import { Notify } from "quasar";
import axios from "axios";
import moment from "moment-timezone";
import store from "src/store";
import { ensureActivityOperation } from "src/utils/activityOperation";
import { LoadingMonitor } from "src/utils/LoadingMonitor";
import { handleApiError, installNotifySuppression } from "src/utils/apiErrorNotify";

// Be careful when using SSR for cross-request state pollution
// due to creating a Singleton instance here;
// If any client changes this (global) instance, it might be a
// good idea to move this instance creation inside of the
// "export default () => {}" function below (which runs individually
// for each client)

const API_BASE = process.env.API_BASE_URL;
const INTEGRATION_HUB_API_BASE = process.env.INTEGRATION_HUB_BASE_URL;

const api = axios.create({ baseURL: API_BASE });
const integrationHubApi = INTEGRATION_HUB_API_BASE ? axios.create({ baseURL: INTEGRATION_HUB_API_BASE }) : null;
let lastQbReconnectNotifyAt = 0;

// const route = (name, call, params) => {
//   let route = API_ROUTES[name];

//   if (call instanceof Object) {
//     params = call;
//   } else if (call) {
//     route = `${route}/${call}`;
//   }

//   return [route, params];
// };

const route = (name, call, params) => {
  let route = API_ROUTES[name];

  if (call instanceof Object) {
    params = call;
  } else if (call) {
    call = call.toString();
    if (!route.endsWith("/") && !call.startsWith("/") && !route.endsWith("=")) {
      route += "/";
    }
    route += call;
  }

  return [route, params];
};

const shouldSuppressGlobalApiToast = (config) => {
  const url = config?.url || "";
  return (
    url.includes("/current-company/preferences/inventory-defaults") ||
    url.includes("/stock-return") ||
    url.includes("/status-changes") ||
    url.includes("/external-vendor-products-product-lookup") ||
    url.includes("/current-company/default-order-contact") ||
    // The Brand facet owns its own loading, empty, and error presentation in
    // the filter drawer; a global toast on top of it would be noise.
    url.includes("/marketplace/facets") ||
    // Authenticated Storefront pages own their loading and eligibility errors.
    url.includes("/brands/storefronts/") ||
    // Generic Order pages preserve entered state and render field/server errors.
    url.includes("/generic-orders") ||
    // Price Group license search and assignment show their own messages, such as a
    // license that is no longer Active. A global toast would duplicate them.
    url.includes("/pricing-templates/search-registry-licenses") ||
    // The seller-state default is optional. Without it, the picker starts on all states.
    url.includes("/pricing-templates/registry-search-defaults") ||
    // The buyer picker shows registry buyer errors and not-found notices itself.
    url.includes("/licenses/registry-buyers") ||
    /\/pricing-templates\/\d+\/assign(?:$|\?)/.test(url)
  );
};

// The manual order page shows these placement errors itself, as the changed-address dialog or the
// placement-blocked sentence. Other errors from the same endpoint still show the global toast.
const PAGE_HANDLED_ERROR_CODES = ["facility_address_changed", "placement_blocked"];
const isPageHandledError = (error) =>
  (error?.config?.url || "").includes("/orders/manual") &&
  PAGE_HANDLED_ERROR_CODES.includes(error?.response?.data?.code);

const handleNoAuth = (url) => {
  if (!url) {
    // log out
    const event = new Event("unauthenticatedApiResponse");
    document.dispatchEvent(event);
    return;
  }

  // Check specifically for METRC-related endpoints - double check this applies here!
  if (url.includes("/metrc/")) {
    console.log("METRC API authentication error - handling separately");
    // Dispatch a specific event for METRC auth failures
    const metrcEvent = new Event("metrcAuthenticationError");
    document.dispatchEvent(metrcEvent);

    // Still dispatch the regular event, but with a flag indicating it's METRC-related
    const customEvent = new CustomEvent("unauthenticatedApiResponse", {
      detail: { isMetrcRelated: true },
    });
    document.dispatchEvent(customEvent);
  } else {
    // Regular auth failure - normal logout behavior
    const event = new Event("unauthenticatedApiResponse");
    document.dispatchEvent(event);
  }
};

// request interceptor for performance tracking
api.interceptors.request.use((config) => {
  ensureActivityOperation(config);
  // Generate a unique ID for tracking this request
  config.requestId =
    Date.now().toString(36) + Math.random().toString(36).substring(2);

  // Start monitoring this request
  const endpoint = `${config.method?.toUpperCase() || "GET"} ${config.url}`;
  LoadingMonitor.start(`API:${endpoint}`, {
    requestId: config.requestId,
    url: config.url,
    method: config.method,
  });

  return config;
});

// response interceptor for performance tracking
api.interceptors.response.use(
  (response) => {
    const config = response.config;
    const endpoint = `${config.method?.toUpperCase() || "GET"} ${config.url}`;

    // End timing
    LoadingMonitor.end(`API:${endpoint}`);

    return response;
  },
  (error) => {
    // Safely check if config exists and handle errors without assuming response exists
    if (error.config) {
      const endpoint = `${error.config.method?.toUpperCase() || "GET"} ${
        error.config.url
      }`;
      LoadingMonitor.end(`API:${endpoint}`);
    }

    // Skip toasts for intentionally canceled requests
    if (axios.isCancel(error)) {
      return Promise.reject(error);
    }

    // Safely check if response exists before accessing status
    if (error.response && error.response.status === 401) {
      // Pass the URL to distinguish METRC endpoints
      handleNoAuth(error.config?.url);
    } else if (
      !error.response ||
      error.response.status === 422 ||
      error.response.status >= 500
    ) {
      if (!shouldSuppressGlobalApiToast(error.config) && !isPageHandledError(error)) {
        handleApiError(error);
      }
    }

    return Promise.reject(error);
  }
);

if (integrationHubApi) {
  integrationHubApi.interceptors.response.use(
    (response) => response,
    (error) => {
      const status = error?.response?.status;

      if (status === 401) {
        const isIntegrationPage =
          typeof window !== "undefined" &&
          window.location?.pathname?.includes("/my/company/integrations/");

        if (isIntegrationPage) {
          const now = Date.now();
          if (now - lastQbReconnectNotifyAt > 4000) {
            lastQbReconnectNotifyAt = now;
            Notify.create({
              color: "warning",
              position: "top",
              message:
                "QuickBooks authorization expired. Please reconnect your integration.",
              timeout: 6000,
            });
          }
        }
      }

      return Promise.reject(error);
    }
  );
}

const $API = Object.keys(API_ROUTES).reduce((rtn, name) => {
  rtn[name] = {
    GET(call, params) {
      [call, params] = route(name, call, params);

      let result = api.get(call, { params });

      result.catch((error) => {
        if (error.response && error.response.status === 401) {
          handleNoAuth(error.config?.url);
        }
      });

      return result;
    },
    POST(call, params) {
      [call, params] = route(name, call, params);

      let result = api.post(call, params);

      result.catch((error) => {
        if (error.response && error.response.status === 401) {
          handleNoAuth(error.config?.url);
        }
      });

      return result;
    },
    PUT(call, params) {
      [call, params] = route(name, call, params);

      let result = api.put(call, params);

      result.catch((error) => {
        if (error.response && error.response.status === 401) {
          handleNoAuth(error.config?.url);
        }
      });

      return result;
    },
    DELETE(call, payload = null) {
      [call] = route(name, call);

      const config = payload ? { data: payload } : undefined;
      let result = api.delete(call, config);

      result.catch((error) => {
        if (error.response && error.response.status === 401) {
          handleNoAuth(error.config?.url);
        }
      });

      return result;
    },
    GET_FILE(call, params) {
      [call, params] = route(name, call, params);

      const result = api.get(call, {
        params,
        responseType: 'blob'
      });

      result.catch((error) => {
        if (error.response && error.response.status === 401) {
          handleNoAuth(error.config?.url);
        }
      });

      return result.then(res => res.data);
    },
    POST_FILE_RES(call, data, config = {}) {
      [call] = route(name, call);
      let result = api.post(call, data, config);
      result.catch((error) => {
        if (error.response && error.response.status === 401) {
          handleNoAuth(error.config?.url);
        }
      });
      return result;
    },

  };

  return rtn;
}, {});

export default boot(({ app, store }) => {
  //Set Authorization header and other required for API.
  api.defaults.headers.common[
    "Authorization"
  ] = `Bearer ${store.state.auth.token}`;
  api.defaults.headers.common["Accept"] = "application/json";
  api.defaults.headers.common["Content-Type"] = "application/json";
  api.defaults.headers.common["Timezone"] = moment.tz.guess();

  if (integrationHubApi) {
    integrationHubApi.defaults.headers.common["Authorization"] = `Bearer ${store.state.auth.token}`;
    integrationHubApi.defaults.headers.common["Accept"] = "application/json";
    integrationHubApi.defaults.headers.common["Content-Type"] = "application/json";
  }

  app.config.globalProperties.$API = $API;
  app.config.globalProperties.integrationHubApi = integrationHubApi;

  installNotifySuppression(app);
});

export { api, $API, integrationHubApi };
